import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import moment from "moment";
import { selectProductMaterialData } from "../../selectors/collaborativeForm.selector";
import { selectSupplierId } from "../../selectors/login.selector";
import { getProductMaterialData } from "../../reducers/collaborativeForm.reducer";

const ProductMaterialsDataTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { supplierId: urlSupplierId } = useParams();
  const loginSupplierId = useSelector(selectSupplierId);
  const effectiveSupplierId = urlSupplierId || loginSupplierId;

  useEffect(() => {
    if (effectiveSupplierId) {
      dispatch(getProductMaterialData({ supplierId: effectiveSupplierId }));
    } else {
    }
  }, [dispatch, effectiveSupplierId]);

  const productMaterialData = useSelector(selectProductMaterialData);

  // Format date helper
  const getValidDate = (dateString, format = "MM/DD/YYYY") => {
    if (!dateString) return "-";
    const date = moment(dateString);
    return date.isValid() ? date.format(format) : "-";
  };

  // Get chip styling based on status type
  const getChipStyling = (status, isSubmissionStatus = false) => {
    if (isSubmissionStatus) {
      // Submission status styling
      switch (status?.toLowerCase()) {
        case "submitted":
        case "submitted_with_note":
          return { backgroundColor: "#E6F4EA", color: "#137333", border: "1px solid rgba(18, 183, 106, 0.30)" };
        default:
          return { backgroundColor: "#F2F4F7", color: "#344054", border: "1px solid #D0D5DD" };
      }
    }
    
    // Brand approval status styling
    if (!status || status === "Pending") {
      return { backgroundColor: "#F2F4F7", color: "#667085", border: "1px solid #D0D5DD" };
    }
    if (status === "Approved") {
      return { backgroundColor: "#E6F4EA", color: "#137333", border: "1px solid rgba(18, 183, 106, 0.30)" };
    }
    if (status === "Not Approved") {
      return { backgroundColor: "#FEE2E2", color: "#B91C1C", border: "1px solid rgba(185, 28, 28, 0.30)" };
    }
    return { backgroundColor: "#FEF3C7", color: "#B45309", border: "1px solid rgba(234, 170, 8, 0.30)" };
  };

  // Render submission status chip
  const renderSubmissionStatusChip = (status) => {
    let displayStatus;
    const statusLower = status?.toLowerCase();
    
    if (statusLower === "submitted") {
      displayStatus = "Submitted";
    } else if (statusLower === "submitted_with_note") {
      displayStatus = "Submitted with Note";
    } else {
      displayStatus = "Not Submitted";
    }
    
    return (
      <Chip
        label={displayStatus}
        sx={{ ...getChipStyling(status, true), fontSize: "14px", fontWeight: 500, borderRadius: "6px", padding: "4px 10px" }}
      />
    );
  };

  // Render brand approval status chip
  const renderBrandApprovalChip = (status) => {
    const displayStatus = (!status || status === "Pending") ? "Pending" : status;
    return (
      <Chip
        label={displayStatus}
        sx={{ ...getChipStyling(status), fontSize: "14px", fontWeight: 500, borderRadius: "6px", padding: "4px 10px" }}
      />
    );
  };

  const handleFormClick = (formId, status) => {
    const currentSupplierId = urlSupplierId || loginSupplierId;
    const queryParams = currentSupplierId ? `?supplierId=${currentSupplierId}` : "";

    if (status?.toLowerCase() === "submitted") {
      // Navigate to material details page for submitted forms
      const path = currentSupplierId 
        ? `/suppliers/${currentSupplierId}/material/${formId}` 
        : `/material/${formId}`;
      navigate(path);
    } else {
      // Navigate to form page for all other statuses
      navigate(`/form/${formId}${queryParams}`);
    }
  };

  return (
    <>
      <Box sx={{ padding: "24px" }}>
        {productMaterialData?.map((material, index) => {
          const brandApprovalStatus = material.brandApprovalStatus;
          
          return (
          <Card
            key={material._id || index}
            sx={{
              marginBottom: "24px",
              borderRadius: "12px",
              border: "1px solid #EAECF0",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0px 2px 2px 0px rgba(16, 24, 40, 0.15)",
                elevation: 2,
                cursor: "pointer",
              },
            }}
            onClick={() => handleFormClick(material._id, material.status)}
          >
            <CardContent sx={{ padding: "24px" }}>
              {/* Material Header */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#101828",
                  marginBottom: "16px",
                }}
              >
                Reformation Traceability Form
              </Typography>


              {/* Material Details Table */}
              <Table sx={{ width: "100%" }}>
                <TableBody>
                  <TableRow
                    sx={{ "& > td": { border: "none", padding: "8px 0" } }}
                  >
                    {/* Created Date */}
                    <TableCell sx={{ width: "15%", verticalAlign: "top" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#667085",
                          marginBottom: "4px",
                        }}
                      >
                        Request Date
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#101828",
                        }}
                      >
                        {getValidDate(
                          material.createdDate || material.createdAt
                        )}
                      </Typography>
                    </TableCell>

                    {/* Material Type (Clickable) */}
                    <TableCell sx={{ width: "25%", verticalAlign: "top" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#667085",
                          marginBottom: "4px",
                        }}
                      >
                        Material/Product Name
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#101828",
                        }}
                      >
                        {material.materialName ||
                          material.formMetadata?.title ||
                          `Material ${index + 2}`}
                      </Typography>
                    </TableCell>

                    {/* Submission Status */}
                    <TableCell sx={{ width: "20%", verticalAlign: "top" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#667085",
                          marginBottom: "4px",
                        }}
                      >
                        Submission Status
                      </Typography>
                      <Box>{renderSubmissionStatusChip(material.status)}</Box>
                    </TableCell>

                    {/* Brand Approval Status */}
                    <TableCell sx={{ width: "20%", verticalAlign: "top" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#667085",
                          marginBottom: "4px",
                        }}
                      >
                        Brand Approval Status
                      </Typography>
                      <Box>{renderBrandApprovalChip(brandApprovalStatus)}</Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          );
        })}

        {/* Empty State */}
        {(!productMaterialData || productMaterialData.length === 0) && (
          <Card
            sx={{
              padding: "48px",
              textAlign: "center",
              border: "1px solid #EAECF0",
              borderRadius: "12px",
            }}
          >
            <Typography sx={{ fontSize: "16px", color: "#667085" }}>
              No materials available
            </Typography>
          </Card>
        )}
      </Box>
    </>
  );
};

export default ProductMaterialsDataTable;
